import { of } from "rxjs";
import { Store } from "@ngrx/store";
import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";
import { FeatureFamilyActions } from "./feature-family.actions";
import { selectLoggedInUser } from "../../common/state/user/user.selectors";
import { FeatureFamilyService } from "../service/feature-family.service";
import { ToastType } from "../../common/models";
import { ToastService } from "../../common/services/toast/toast.service";

const INVALID_USER_ERROR = Error("Logged in user not valid");
const ERROR_RETRIEVING = Error("Error retrieving feature families");
const ERROR_CREATING = Error("Error creating feature family");
const ERROR_UPDATING = Error("Error updating feature family");
const ERROR_DELETING = Error("Error deleting feature family");
const INVALID_USER_RESPONSE = { error: INVALID_USER_ERROR };
const ERROR_RETRIEVING_RESPONSE = { error: ERROR_RETRIEVING };
const ERROR_CREATING_RESPONSE = { error: ERROR_CREATING };
const ERROR_UPDATING_RESPONSE = { error: ERROR_UPDATING };

@Injectable()
export class FeatureFamilyEffects {
    private actions$ = inject(Actions);
    private store$ = inject(Store);
    private service = inject(FeatureFamilyService);
    private toastService = inject(ToastService);

    loadFamilies$ = createEffect(() => this.actions$.pipe(
        ofType(FeatureFamilyActions.loadFamilies),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([_, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(FeatureFamilyActions.loadFamiliesFailure(INVALID_USER_RESPONSE));
            }

            return this.service.getAll().pipe(
                map(response => {
                    return FeatureFamilyActions.loadFamiliesSuccess({ families: response });
                }),
                catchError(error => {
                    console.error(error);
                    return of(FeatureFamilyActions.loadFamiliesFailure(ERROR_RETRIEVING_RESPONSE));
                })
            );
        })
    ));

    createFamily$ = createEffect(() => this.actions$.pipe(
        ofType(FeatureFamilyActions.createFamily),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(FeatureFamilyActions.createFamilyFailure(INVALID_USER_RESPONSE));
            }

            return this.service.create(action.family).pipe(
                map(response => {
                    return FeatureFamilyActions.createFamilySuccess({ family: response });
                }),
                catchError(error => {
                    console.error(error);
                    return of(FeatureFamilyActions.createFamilyFailure(ERROR_CREATING_RESPONSE));
                })
            );
        })
    ));

    updateFamily$ = createEffect(() => this.actions$.pipe(
        ofType(FeatureFamilyActions.updateFamily),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(FeatureFamilyActions.updateFamilyFailure(INVALID_USER_RESPONSE));
            }

            return this.service.update(action.id, action.family).pipe(
                map(response => {
                    return FeatureFamilyActions.updateFamilySuccess({ family: response });
                }),
                catchError(error => {
                    console.error(error);
                    return of(FeatureFamilyActions.updateFamilyFailure(ERROR_UPDATING_RESPONSE));
                })
            );
        })
    ));

    deleteFamily$ = createEffect(() => this.actions$.pipe(
        ofType(FeatureFamilyActions.deleteFamily),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(FeatureFamilyActions.deleteFamilyFailure({ family: action.family, error: INVALID_USER_ERROR }));
            }

            return this.service.delete(action.family.id).pipe(
                map(() => {
                    return FeatureFamilyActions.deleteFamilySuccess({ family: action.family });
                }),
                catchError((error: Error) => {
                    console.error(error);
                    this.toastService.showToast(
                        error?.message || 'Failed to delete feature family',
                        ToastType.ERROR
                    );
                    return of(FeatureFamilyActions.deleteFamilyFailure({ family: action.family, error }));
                })
            );
        })
    ));
}
